import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAuthToken } from "@/modules/safecheck/auth";
import {
  deleteNotification,
  fetchNotifications,
  mapApiNotification,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/modules/safecheck/api/adminOpsApi";

type NotificationContextValue = {
  notifications: Notification[];
  unreadCount: number;
  isOpen: boolean;
  togglePanel: () => void;
  closePanel: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  refresh: () => Promise<void>;
  /** Ajoute des notifications locales (ex. alertes dashboard) sans doublon d'id. */
  upsertLocal: (items: Notification[]) => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      setNotifications([]);
      return;
    }

    try {
      const response = await fetchNotifications();
      if (!response.table_exists) {
        setNotifications((prev) => prev.filter((n) => n.id.startsWith("local:") || n.id.startsWith("enterprise-alert-")));
        return;
      }
      const fromApi = (response.notifications ?? []).map(mapApiNotification);
      setNotifications((prev) => {
        const locals = prev.filter(
          (n) => n.id.startsWith("local:") || n.id.startsWith("enterprise-alert-"),
        );
        if (locals.length === 0) return fromApi;
        const apiIds = new Set(fromApi.map((n) => n.id));
        return [...locals.filter((n) => !apiIds.has(n.id)), ...fromApi];
      });
    } catch {
      setNotifications((prev) =>
        prev.filter((n) => n.id.startsWith("local:") || n.id.startsWith("enterprise-alert-")),
      );
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const togglePanel = useCallback(() => setIsOpen((prev) => !prev), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    if (getAuthToken()) {
      void markNotificationRead(id).catch(() => undefined);
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    if (getAuthToken()) {
      void markAllNotificationsRead().catch(() => undefined);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (getAuthToken()) {
      void deleteNotification(id).catch(() => undefined);
    }
  }, []);

  const clearAll = useCallback(() => {
    const ids = notifications.map((n) => n.id);
    setNotifications([]);
    setIsOpen(false);
    if (getAuthToken()) {
      ids.forEach((id) => {
        void deleteNotification(id).catch(() => undefined);
      });
    }
  }, [notifications]);

  const upsertLocal = useCallback((items: Notification[]) => {
    if (items.length === 0) return;
    setNotifications((prev) => {
      const existing = new Set(prev.map((n) => n.id));
      const fresh = items.filter((n) => !existing.has(n.id));
      if (fresh.length === 0) return prev;
      return [...fresh, ...prev];
    });
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isOpen,
        togglePanel,
        closePanel,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
        refresh,
        upsertLocal,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}
